import { execSync } from 'child_process';
import fs from 'fs';
import _ from 'lodash';
import semver from 'semver';
import YAML from 'yaml';

import Configs, { IConfig } from '../common/configs';
import Logger from '../common/logger';

export interface IMigration {
  name: string;
  version: string;
  up: (configs: IConfig) => Promise<void>;
  down: (configs: IConfig) => Promise<void>;
}

export function loadYAMLFile(filename: string): object {
  try {
    const file = fs.readFileSync(filename, 'utf8');
    return YAML.parse(file);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error loading file ${filename}`);
    // eslint-disable-next-line no-console
    console.error(error);
    return {};
  }
}

export function loadJSONFile(filename: string): object {
  try {
    const file = fs.readFileSync(filename, 'utf8');
    return JSON.parse(file);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error loading file ${filename}`);
    // eslint-disable-next-line no-console
    console.error(error);
    return {};
  }
}

export function loadConfigFile(filename: string): object {
  const file = fs.readFileSync(filename, 'utf8');
  let content = {};
  try {
    content = JSON.parse(file);
  } catch {
    // eslint-disable-next-line no-console
    Logger.error('Not a JSON config');
  }
  if (!_.isEmpty(content)) {
    Logger.debug(`Loading JSON configs from ${filename}`);
    return content;
  }
  try {
    content = YAML.parse(file);
  } catch {
    // eslint-disable-next-line no-console
    Logger.error('Not a YAML config');
  }
  if (!_.isEmpty(content)) {
    Logger.debug(`Loading YAML configs from ${filename}`);
    return content;
  }
  // eslint-disable-next-line no-console
  Logger.debug('Configs loaded as string');
  return { content: file.toString() };
}

export function saveJSONFile(filename: string, content: any): void {
  try {
    const jsonContent = JSON.stringify(content, undefined, 2);
    fs.writeFileSync(filename, jsonContent);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
}

export function currentSeconds(): number {
  return Math.floor(new Date().getTime() / 1000);
}

export function dump(variable: any): string {
  return _.isObject(variable) ? JSON.stringify(variable) : variable;
}

export function fixPermissions(): void {
  const { app } = Configs.configs;
  const { user, applicationPath } = app;
  Logger.silly('Fix permissions');
  const permissionCommand = `sudo chown -R ${user}:${user} ${applicationPath}`;
  if (process.platform === 'linux' && process.arch === 'arm') {
    execSync(permissionCommand, { cwd: applicationPath });
  }
}

export function executeMigrations(migrations: IMigration[] = []): boolean {
  const { version: previousVersion = '0.0.0', fbxfile } = Configs.configs;
  const { version: currentVersion } = fbxfile;
  let updateVersion = false;
  if (previousVersion !== currentVersion) {
    updateVersion = true;
  }
  // First execution
  if (previousVersion === '0.0.0') {
    Logger.info('First execution, no migrations to execute');
  } else {
    Logger.info(`Executing migrations from ${previousVersion} to ${currentVersion}`);

    // filter migration based to previous and current version
    // previous < version <= current
    let migrationsToExecute = migrations.filter((m) => {
      const a = semver.lt(previousVersion, m.version);
      const b = semver.gte(currentVersion, m.version);
      return a && b;
    });

    // sort migration by name
    migrationsToExecute = migrationsToExecute.sort((a, b) => (a.name > b.name ? 1 : -1));

    if (migrationsToExecute.length > 0) {
      fixPermissions();

      // execute migrations in sequence
      let stopExecution = false;
      migrationsToExecute.forEach((migration) => {
        if (stopExecution) {
          Logger.warn(`Skip migration ${migration.name}`);
        } else {
          try {
            Logger.info(`Executing migration ${migration.name}...`);
            migration.up(Configs.configs);
          } catch (e) {
            Logger.error(`Fail to execute migration ${migration.name}`);
            Logger.error(e);
            stopExecution = true;
          } finally {
            fixPermissions();
          }
        }
      });

      Configs.save();

      Logger.info('Migrations completed');
    } else {
      Logger.info('No migrations to execute');
    }
  }

  if (updateVersion) {
    Configs.set('version', currentVersion, true);
  }

  return true;
}

export function checkStartRequirements(): string[] {
  const { fbxfile, app } = Configs.configs;
  const { requiredLicenses = [] } = fbxfile;
  const { serial } = app;
  const reasons = [];
  // if the module has requiredLicenses but the core is not sending the device serial (core too old)
  // then block the start
  if (!_.isEmpty(requiredLicenses) && _.isEmpty(serial)) {
    reasons.push('The core version is too old. Please update the core package.');
  }
  return reasons;
}

export function getAliveDestinations(): string[] {
  const coreServiceName = process.env.CORE_SERVICE_NAME || 'fbx-core';
  const moduleDestinations = process.env.ALIVE_DESTINATIONS || '';
  const aliveDestinations = [
    coreServiceName,
    ..._.compact(moduleDestinations.split(',').map((d) => _.trim(d)))
  ];
  return aliveDestinations;
}
